/**
 * DraftGenerator Component
 * UI for generating academic sections with user context and selected citations
 */

import React, { useState, useEffect } from 'react'
import { logError } from '@utils/logger'
import {
  DocumentTextIcon,
  XMarkIcon,
  SparklesIcon,
  BookOpenIcon,
  CheckIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import clsx from 'clsx'

import { researchDraftsAPI, type DraftGenerationRequest, type SectionType } from '@/api/researchDrafts'
import { citationLibraryAPI, type CitationLibraryItem } from '@/api/citationLibrary'
import { DiffView } from './DiffView'
import { useQueryClient } from '@tanstack/react-query'

interface DraftGeneratorProps {
  documentId: string
  documentContent: string
  cursorPosition?: number // Cursor position in document for insertion
  onContentInsert?: (content: string, position?: number) => void
  onClose?: () => void
  isOpen: boolean
}

export const DraftGenerator: React.FC<DraftGeneratorProps> = ({
  documentId,
  documentContent,
  cursorPosition,
  onContentInsert,
  onClose,
  isOpen,
}) => {
  const queryClient = useQueryClient()
  
  // Form state
  const [sectionType, setSectionType] = useState<SectionType>('introduction')
  const [userContext, setUserContext] = useState('')
  const [selectedCitations, setSelectedCitations] = useState<string[]>([])
  const [options, setOptions] = useState({
    tone: 'academic',
    length: 'medium',
    citation_density: 'normal',
    custom_instructions: '',
  })

  // Data state
  const [availableCitations, setAvailableCitations] = useState<CitationLibraryItem[]>([])
  const [isLoadingCitations, setIsLoadingCitations] = useState(false)

  // Generation state
  const [isGenerating, setIsGenerating] = useState(false)
  const [generatedContent, setGeneratedContent] = useState<string | null>(null)
  const [showPreview, setShowPreview] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Load citations from library
  useEffect(() => {
    if (isOpen && documentId) {
      loadCitations()
    }
  }, [isOpen, documentId])

  const loadCitations = async () => {
    setIsLoadingCitations(true)
    try {
      const citations = await citationLibraryAPI.getCitations(documentId)
      setAvailableCitations(citations || [])
    } catch (error: any) {
      logError('Error loading citations:', error)
    } finally {
      setIsLoadingCitations(false)
    }
  }

  const handleGenerate = async () => {
    // Validate user context
    if (!userContext.trim() || userContext.trim().length < 10) {
      return
    }

    setIsGenerating(true)
    setErrorMessage(null)
    setGeneratedContent(null)
    setShowPreview(false)

    try {
      const request: DraftGenerationRequest = {
        section_type: sectionType,
        user_context: userContext.trim(),
        document_id: documentId,
        selected_citation_ids: selectedCitations.length > 0 ? selectedCitations : undefined,
        options: {
          tone: options.tone,
          length: options.length,
          citation_density: options.citation_density,
          custom_instructions: options.custom_instructions || undefined,
        },
      }

      const response = await researchDraftsAPI.generateDraft(request)
      
      setGeneratedContent(response.content)
      setShowPreview(true)
      
      // Invalidate token usage queries after successful draft generation
      queryClient.invalidateQueries({ queryKey: ['token-usage', 'document', documentId] })
      queryClient.invalidateQueries({ queryKey: ['token-usage', 'user-stats'] })
    } catch (error: any) {
      logError('Error generating draft:', error)
      const detail = error?.response?.data?.detail
      let message = 'Failed to generate draft'
      if (typeof detail === 'string') {
        message = detail
      } else if (detail && typeof detail === 'object') {
        message = detail.message || detail.error || message
      } else if (error?.message) {
        message = error.message
      }
      setErrorMessage(message || 'Something went wrong. Please contact the administrator.')
      try {
        toast.error(message || 'Something went wrong. Please contact the administrator.')
      } catch {
        // toast may not be mounted; inline message above will still show
      }
    } finally {
      setIsGenerating(false)
    }
  }

  const handleAccept = () => {
    if (generatedContent && onContentInsert) {
      onContentInsert(generatedContent, cursorPosition)
      // Reset form
      setGeneratedContent(null)
      setShowPreview(false)
      setUserContext('')
      setSelectedCitations([])
      if (onClose) {
        onClose()
      }
    }
  }

  const handleReject = () => {
    setGeneratedContent(null)
    setShowPreview(false)
  }

  const toggleCitation = (citationId: string) => {
    setSelectedCitations(prev =>
      prev.includes(citationId)
        ? prev.filter(id => id !== citationId)
        : [...prev, citationId]
    )
  }

  if (!isOpen) return null

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <SparklesIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Generate Section
          </h2>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-1 rounded-md text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {errorMessage && (
          <div className="rounded-md border border-red-400 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-xs text-red-700 dark:text-red-200">
            {errorMessage}
          </div>
        )}
        {showPreview && generatedContent ? (
          // Show diff view
          <DiffView
            originalContent=""
            newContent={generatedContent}
            onAccept={handleAccept}
            onReject={handleReject}
            isInsertion={true}
            title={`Generated ${sectionType.replace('_', ' ')} Section`}
          />
        ) : (
          // Show form
          <>
            {/* Section Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Section Type
              </label>
              <select
                value={sectionType}
                onChange={(e) => setSectionType(e.target.value as SectionType)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={isGenerating}
              >
                <option value="introduction">Introduction</option>
                <option value="literature_review">Literature Review</option>
                <option value="methodology">Methodology</option>
                <option value="results_discussion">Results & Discussion</option>
                <option value="conclusion">Conclusion</option>
              </select>
            </div>

            {/* User Context - REQUIRED */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Context <span className="text-red-500">*</span>
              </label>
              <textarea
                value={userContext}
                onChange={(e) => setUserContext(e.target.value)}
                placeholder="Describe what you want to write about. This is required and helps the AI understand your needs. For example: 'I want to introduce the problem of climate change in urban areas and review recent studies on mitigation strategies.'"
                rows={6}
                className={clsx(
                  'w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100',
                  'focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                  'resize-none',
                  userContext.trim().length > 0 && userContext.trim().length < 10
                    ? 'border-yellow-500'
                    : 'border-gray-300 dark:border-gray-600'
                )}
                disabled={isGenerating}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Minimum 10 characters. Be specific about what you want to write.
              </p>
            </div>

            {/* Citation Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Citations (Optional)
              </label>
              {isLoadingCitations ? (
                <div className="text-sm text-gray-500 dark:text-gray-400">Loading citations...</div>
              ) : availableCitations.length === 0 ? (
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  No citations in library. Add citations first.
                </div>
              ) : (
                <div className="max-h-48 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg p-2 space-y-2">
                  {availableCitations.map((citation) => (
                    <label
                      key={citation.id}
                      className={clsx(
                        'flex items-start gap-2 p-2 rounded cursor-pointer transition-colors',
                        selectedCitations.includes(citation.id)
                          ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-700'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700 border border-transparent'
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={selectedCitations.includes(citation.id)}
                        onChange={() => toggleCitation(citation.id)}
                        className="mt-1"
                        disabled={isGenerating}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {citation.title}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {Array.isArray(citation.authors) 
                            ? citation.authors.join(', ')
                            : citation.authors}
                          {citation.year && ` • ${citation.year}`}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
              {selectedCitations.length > 0 && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {selectedCitations.length} citation{selectedCitations.length !== 1 ? 's' : ''} selected
                </p>
              )}
            </div>

            {/* Options */}
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tone
                </label>
                <select
                  value={options.tone}
                  onChange={(e) => setOptions({ ...options, tone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  disabled={isGenerating}
                >
                  <option value="academic">Academic</option>
                  <option value="formal">Formal</option>
                  <option value="concise">Concise</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Length
                </label>
                <select
                  value={options.length}
                  onChange={(e) => setOptions({ ...options, length: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  disabled={isGenerating}
                >
                  <option value="short">Short (~300 words)</option>
                  <option value="medium">Medium (~500 words)</option>
                  <option value="long">Long (~800 words)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Custom Instructions (Optional)
                </label>
                <textarea
                  value={options.custom_instructions}
                  onChange={(e) => setOptions({ ...options, custom_instructions: e.target.value })}
                  placeholder="e.g., Focus on recent developments, emphasize practical applications..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 resize-none"
                  disabled={isGenerating}
                />
              </div>
            </div>

            {/* Generate Button */}
            <button
              onClick={handleGenerate}
              disabled={isGenerating || !userContext.trim() || userContext.trim().length < 10}
              className={clsx(
                'w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors',
                isGenerating || !userContext.trim() || userContext.trim().length < 10
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              )}
            >
              {isGenerating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <SparklesIcon className="w-5 h-5" />
                  Generate Section
                </>
              )}
            </button>
          </>
        )}
      </div>
    </div>
  )
}

